import { Sites, SpecialAnchorTypes, ToolBaseTypes } from '@labradorsports/constants';
import SportConfigs from '@labradorsports/sports';
import { arraySlidingWindow } from '@labradorsports/utils';

import { Move } from '../../models/index.js';
import { PluginContext } from '../../plugins/api.js';
import {
    createLines,
    getPreviousMoveAnchor,
    snapMoveEndpointToAngleGrid,
    snapMoveToPlayerLine,
    defaultMove,
    applyOffset,
    getMidpoint,
} from '../../utils/index.js';
import { getFootballToolFrameIdx } from '../move-placement-frame.js';
import PlayConfig from '../play-config.js';

const { PlayerPositions } = SportConfigs[Sites.footballlab];
const { ToolTypes } = PlayConfig;

function getDefaultMove(frame, closest, origin, type) {
    const move = defaultMove(frame.field, closest, type, closest.id);

    const closestOffset = (offset) => applyOffset(origin, offset);

    if (
        [PlayerPositions.WIDE_RECEIVER, PlayerPositions.RUNNING_BACK, PlayerPositions.TIGHT_END].includes(
            closest.props.role
        )
    ) {
        if (type === ToolTypes.PRESNAP) {
            const xOffset = origin.x > frame.field.width / 2 ? -5 : 5;
            move.anchors[0] = closestOffset({
                x: xOffset,
            });
        } else {
            move.anchors[0] = closestOffset({
                y: -5,
            });
        }
    }

    if (
        [
            PlayerPositions.CENTER,
            PlayerPositions.LEFT_TACKLE,
            PlayerPositions.LEFT_GUARD,
            PlayerPositions.RIGHT_TACKLE,
            PlayerPositions.RIGHT_GUARD,
        ].includes(closest.props.role)
    ) {
        move.anchors[0] =
            type === ToolTypes.PRESNAP
                ? closestOffset({
                      x: -5,
                  })
                : closestOffset({
                      y: -2,
                  });

        move.type = type === ToolTypes.MOVE ? ToolTypes.BLOCK : type;
    }

    if (closest.props.role === PlayerPositions.QUARTERBACK) {
        move.anchors[0] = closestOffset({
            y: 2,
        });
    }

    move.anchors = move.anchors.map((anc: any) => {
        return frame.field.clampCoords(anc);
    });

    return move;
}

export default {
    baseType: ToolBaseTypes.MOVE,
    types: [ToolTypes.PRESNAP, ToolTypes.DMOVE, ToolTypes.OMOVE, ToolTypes.MOVE, ToolTypes.BLOCK],
    specialAnchorTypes: [SpecialAnchorTypes.MOVE_ELBOW],
    generateAnchors: ({ play }) => {
        const anchors = [];

        if (!play.fieldSetup) {
            play.frameSnapshots.forEach((frame, frameIdx) => {
                anchors.unshift(
                    ...frame.visibleMoves(play.viewOptions).flatMap((mv) => {
                        const anchors = mv.anchors.map((anc: any, idx: number) => {
                            return {
                                type: mv.type,
                                id: idx,
                                target: mv.target,
                                frameIdx,
                                ...anc,
                            };
                        });

                        if (mv.anchors.length === 1) {
                            anchors.push({
                                type: SpecialAnchorTypes.MOVE_ELBOW,
                                id: 0,
                                target: mv.target,
                                frameIdx,
                                ...getMidpoint(mv.origin, mv.anchors[0]),
                                props: {
                                    moveType: mv.type,
                                    anchorCount: mv.anchors.length,
                                },
                            });
                        }

                        return anchors;
                    })
                );
            });
        }

        return anchors;
    },

    generateSelectedAnchors: ({ play, frame }, selected) => {
        if (selected instanceof Move && play.config.MoveConfig[selected.type].baseType === ToolBaseTypes.MOVE) {
            return arraySlidingWindow([selected.origin, ...selected.anchors], (anchor, nextAnchor, idx) => {
                return {
                    target: selected.target,
                    type: SpecialAnchorTypes.MOVE_ELBOW,
                    id: idx,
                    frameIdx: frame.idx,
                    x: (anchor.x + nextAnchor.x) / 2,
                    y: (anchor.y + nextAnchor.y) / 2,
                    props: {
                        moveType: selected.type,
                        anchorCount: selected.anchors.length,
                    },
                };
            });
        }

        return [];
    },

    generateLines: ({ play }) => {
        if (play.fieldSetup) return [];

        return play.frameSnapshots.flatMap((frame, frameIdx) =>
            frame
                .visibleMoves(play.viewOptions)
                .flatMap(createLines)
                .map((line) => ({
                    ...line,
                    frameIdx,
                }))
        );
    },

    handleDragEnd: (context, anchor, offset) => {
        const { api, fieldViewport, play } = context;

        const frameIdx = getFootballToolFrameIdx(anchor);
        const frame = play.frameSnapshots[frameIdx];

        if (anchor.type === SpecialAnchorTypes.MOVE_ELBOW) {
            let newMove = frame.getMove(anchor.target);

            const prev = getPreviousMoveAnchor(newMove, anchor.id);
            const angleSnapped = snapMoveEndpointToAngleGrid(prev, offset, fieldViewport);
            const anchorCoords = angleSnapped ?? offset;

            if (newMove.anchors.length === anchor.props.anchorCount) {
                newMove = frame.insertAnchor(anchor.target, anchor.id, anchorCoords);
            } else {
                newMove = frame.updateAnchor(anchor.target, anchor.id, anchorCoords);
            }

            api.updateMove(newMove);
        } else if (typeof anchor.id === 'number') {
            const move = frame.getMove(anchor.target);
            const newMove = move.copy();

            if (anchor.type === ToolTypes.OMOVE || anchor.type === ToolTypes.DMOVE) {
                newMove.type = ToolTypes.MOVE;
            }

            // For moves, attempt to snap to a line
            if (anchor.type === ToolTypes.MOVE) {
                const snapLine = snapMoveToPlayerLine(frame, fieldViewport, offset, anchor.target);

                // Snap to the line, and mark the move to remove the player
                if (snapLine) {
                    newMove.props.targetLine = snapLine.id;
                    newMove.updateAnchor(anchor.id, snapLine.origin, play.field);
                } else {
                    newMove.props.targetLine = null;
                    let anchorCoords = offset;
                    const prev = getPreviousMoveAnchor(move, anchor.id);
                    const angleSnapped = snapMoveEndpointToAngleGrid(prev, offset, fieldViewport);
                    if (angleSnapped) {
                        anchorCoords = angleSnapped;
                    }
                    newMove.updateAnchor(anchor.id, anchorCoords, play.field);
                }
            } else {
                newMove.updateAnchor(anchor.id, offset, play.field);
            }

            api.updateMove(newMove, frameIdx);
        } else {
            const closest = frame.findClosestPlayer(
                offset,
                frame.hiddenGoalies(),
                frame.getTargetForToolType(anchor.type)
            );

            if (closest) {
                const newMove = getDefaultMove(frame, closest, closest.origin, anchor.type);

                if (anchor.type === ToolTypes.OMOVE || anchor.type === ToolTypes.DMOVE) {
                    newMove.type = ToolTypes.MOVE;
                }

                api.updateMove(newMove, frameIdx);
            }
        }
    },

    handleDrag({ fieldViewport, frames, play }, anchor, offset, clamped) {
        const frameIdx = getFootballToolFrameIdx(anchor);
        const frame = frames[frameIdx];

        if (anchor.type === SpecialAnchorTypes.MOVE_ELBOW) {
            const move = frame.getMove(anchor.target);

            const prev = getPreviousMoveAnchor(move, anchor.id);
            const angleSnapped = snapMoveEndpointToAngleGrid(prev, clamped, fieldViewport);
            const anchorCoords = angleSnapped ?? clamped;

            if (move.anchors.length === anchor.props.anchorCount) {
                frame.insertAnchor(anchor.target, anchor.id, anchorCoords);
            } else {
                frame.updateAnchor(anchor.target, anchor.id, anchorCoords);
            }
        } else if (anchor.type === ToolTypes.MOVE) {
            const moveOnFrame = frame.getMove(anchor.target);
            const snapLine = snapMoveToPlayerLine(frame, fieldViewport, offset, anchor.target);

            let target = snapLine ? snapLine.origin : clamped;
            if (!snapLine) {
                const prev = getPreviousMoveAnchor(moveOnFrame, anchor.id);
                const angleSnapped = snapMoveEndpointToAngleGrid(prev, target, fieldViewport);
                if (angleSnapped) {
                    target = angleSnapped;
                }
            }

            frame.updateAnchor(anchor.target, anchor.id, target);
        } else {
            // All other move types
            frame.updateAnchor(anchor.target, anchor.id, clamped);

            if (anchor.type === ToolTypes.ZONE) {
                const move = frame.getMove(anchor.target);

                if (anchor.id === move.anchors.length - 1) {
                    const shading = play.field.pieces?.find((piece) => piece.props?.moveId === anchor.target);

                    if (shading) {
                        frame.updateFieldPiece(shading.id, clamped);
                    }
                }
            }
        }
    },

    getDefaultMove({ play, frame }, type, offset) {
        // find the correct target based on the current frame, which offset will be relative to
        const closest = frame.findClosestPlayer(offset, frame.hiddenGoalies(), frame.getTargetForToolType(type));

        // find the origin of the player on the correct frame so the default shape is correct
        const frameIdx = getFootballToolFrameIdx({ type });
        const moveFrame = play.frameSnapshots[frameIdx];

        if (!closest) return null;

        const origin = moveFrame.getPlayer(closest.id).origin;

        return getDefaultMove(frame, closest, origin, type);
    },
} as PlayPlugin<PluginContext>;
